function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React, { MouseEvent, useRef, useState, useEffect } from 'react';
import { useLocation, useParams } from 'react-router';
import { NodeProps, Position, useConnection, useReactFlow, useUpdateNodeInternals, ViewportPortal } from '@xyflow/react';
import { useShallow } from 'zustand/react/shallow';
import clsx from 'clsx';
import { useQueries, UseQueryResult } from '@tanstack/react-query';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import throttle from 'lodash/throttle';
import { Directions } from 'common/components/drop-menu/types';
import { ButtonTheme } from 'common/components/button/constants';
import { ButtonReact } from 'common/components/button';
import { AppState, useCanvasStore } from 'common/report/canvas/store';
import { MultiMetricChart } from 'common/charts/visualization/multi-metric';
import { SegmentFilterController } from 'common/charts/contexts/segment-filter-controller';
import { useTranslation } from 'common/i18n/hooks';
import { ReactErrorBoundary } from 'common/components/react-error-boundary';
import { SpinnerReact } from 'common/components/spinner';
import { SpinnerSize } from 'common/components/spinner/types';
import { SvgIconReact } from 'common/components/svg-icon';
import { ControlBarReact } from 'common/widgets/control-bar';
import type { MetricDefinition, MetricsRequest, MetricsResponse } from 'common/react-query/metrics/types';
import { fetchMetricData } from 'common/react-query/metrics/query';
import { METRICS_REQUEST_STALE_TIME, metricsNodeQueryKeyPrefix } from 'common/report/canvas/react-query';
import { Section } from 'common/components/items-menu/types';
import { InputSize, InputType } from 'common/components/input/types';
import { InputReact } from 'common/components/input';
import { KEY_VALUES } from 'common/util/dom';
import { DateRange } from 'common/widgets/date-range-picker/models/date-range';
import { DropMenuReact } from 'common/components/drop-menu';
import { Visibility, VisibilityChangeEvent } from 'common/components/base/stackable';
import { InsightsLineChartReact } from 'common/report/insights/charts/insights-line-chart';
import { SegmentFilterControllerProvider } from 'common/contexts/react/segment-filter-controller-context';
import { useOpenInNewTab } from 'common/contexts/react/open-in-new-tab-context';
import { COLORS, hasResults } from 'common/report/canvas/utils';
import { MetricChartSize } from 'common/charts/visualization/multi-metric/types';
import { DATASET_MIXPANEL } from 'common/report/constants';
import { insightsFilterAttrsFromPropertyFilter } from 'common/widgets/property-filter-menu/models/insights-filter';
import { FilterClause as FilterClauseType } from 'common/report/insights/util/types';
import { getActiveBoardFilters } from 'apps/boards/src/dashboards-app/util';
import { useMetrics } from 'common/contexts/react/metrics-resource-context';
import { useDataGroups } from 'common/contexts/react/data-group-context';
import { DataGroup } from 'common/widgets/data-group-selector/types';
import { TimeRangeAndComparison, Visibility as CanvasVisibility } from 'iron/common/report/canvas/types';
import { TimeRangesAndComparisonsMenu } from 'common/report/canvas/components/time-ranges-and-comparisons-menu';
import { CustomHandle } from 'common/report/canvas/components/node-handle';
import { AppEdge } from 'common/report/canvas/edges/types';
import { rgbaStringFromHexString } from 'common/util/colors';
import { TrackingEvent } from 'iron/common/report/canvas/tracking';
import { EmptyLineChart } from 'common/components/empty-line-chart';
import { NoDataHeroReact } from 'common/widgets/warning-hero/no-data-hero';
import { NoDataReason, WarningHeroReportName } from 'common/widgets/warning-hero/constants';
import { formatPercent } from 'common/util';
import { ChartTooltip, ChartTooltipProps } from 'common/components/chart-tooltip';
import { TOOLTIP_THROTTLE_TIME } from 'common/components/chart-tooltip/util';
import type { MetricNodeData, MetricNodeType } from './types';
import { buildMetricRequest, computeMetricTooltipSections, convertToInsightsBookmarkParams, convertToInsightsQueryResults, formatMetricValue, generateBookmarkUrl, getMetricNodeTitle, getMetricSegment } from './utils';
import styles from './index.module.styl';
import { SparklineChart } from './sparkline-chart';
enum ControlBarAction {
  Edit = "edit",
  Rename = "rename",
  TimeRanges = "timeRanges",
  Duplicate = "duplicate",
  Delete = "delete",
}
function NodeHeader(_ref: {
  metricNodeId: string;
  metricNodeTitle: string;
  metricDefinition: MetricDefinition;
  isEditing: boolean;
  isViewOnly: boolean;
  setIsChartPreviewOpen: (isOpen: boolean) => void;
  onMetricNameUpdate: (updatedMetricName: string) => void;
  onControlAction: (action: ControlBarAction) => void;
}) {
  let {
    metricNodeId,
    metricNodeTitle,
    metricDefinition,
    isEditing,
    isViewOnly,
    setIsChartPreviewOpen,
    onMetricNameUpdate,
    onControlAction
  }: {
    metricNodeId: string;
    metricNodeTitle: string;
    metricDefinition: MetricDefinition;
    isEditing: boolean;
    isViewOnly: boolean;
    setIsChartPreviewOpen: (isOpen: boolean) => void;
    onMetricNameUpdate: (updatedMetricName: string) => void;
    onControlAction: (action: ControlBarAction) => void;
  } = _ref;
  const reactFlow = useReactFlow();
  const overflowControls: Array<Section<ControlBarAction>> = [{
    items: [{
      label: "Edit",
      icon: "edit",
      value: ControlBarAction.Edit
    }, {
      label: "Rename",
      icon: "text",
      value: ControlBarAction.Rename
    }, {
      label: "Time Ranges",
      icon: "calendar",
      value: ControlBarAction.TimeRanges
    }, {
      label: "Duplicate",
      icon: "duplicate",
      value: ControlBarAction.Duplicate
    }, {
      label: "Delete",
      icon: "trashcan",
      value: ControlBarAction.Delete
    }]
  }];
  return <div className={clsx(styles.nodeHeader, {
    [styles.empty]: !metricNodeTitle,
    [styles.filled]: !!metricNodeTitle && !!metricDefinition
  })} data-sentry-component="NodeHeader" data-sentry-source-file="index.tsx">
      <SvgIconReact className={styles.metricIcon} icon='data-metric-outlined' data-sentry-element="SvgIconReact" data-sentry-source-file="index.tsx" />
      {isEditing ? <InputReact autoFocus type={InputType.Text} size={InputSize.Medium} placeholder='Metric name' value={metricNodeTitle} onBlur={ev => onMetricNameUpdate(ev.target.value as string)} onKeydown={ev => {
      if (ev.key === KEY_VALUES.ENTER) {
        ev.preventDefault();
        (ev.target as HTMLInputElement).blur();
      }
    }} /> : <span title={metricNodeTitle || "New"} className={styles.metricName}>
          {metricNodeTitle || "New"}
        </span>}
      {!!metricDefinition && <ButtonReact className={styles.chartPreviewBtn} icon={"line"} theme={ButtonTheme.Tertiary} onClick={ev => {
      ev.stopPropagation();
      reactFlow.updateNode(metricNodeId, {
        selected: true
      });
      setIsChartPreviewOpen(true);
    }} />}
      {!isViewOnly && <div className={styles.metricControlBar} onClick={ev => ev.stopPropagation()}>
          <ControlBarReact theme={ButtonTheme.Tertiary} overflowIcon={"ellipsis"} overflowControls={overflowControls} overflowMenuWidth={150} overflowMenuAlignment={Directions.Right} onActionSelect={_ref2 => {
        let {
          value
        } = _ref2;
        return onControlAction(value as ControlBarAction);
      }} onMenuChange={isOpen => {
        reactFlow.updateNode(metricNodeId, {
          selected: isOpen
        });
      }} />
        </div>}
    </div>;
}
function NodeBody(_ref3: {
  showConnectMetric: boolean;
  visibility: Record<CanvasVisibility, boolean>;
  dataGroup: DataGroup;
  timeRangesAndComparisons: TimeRangeAndComparison[];
  metricNodeTitle: string;
  metricDefinition: MetricDefinition;
  metricResults: Array<UseQueryResult<MetricsResponse, Error>>;
  metricNodeId: string;
  hideTooltip: boolean;
  selectedTimeRangeIndex: number;
  setActiveMetricNodeId: (nodeId: null | string) => void;
  setLocalSelectedNodeIds: (nodeIds: string[]) => void;
  onActiveTimeRangeChange: (timeRangeAndComparison: TimeRangeAndComparison) => void;
}) {
  let {
    showConnectMetric,
    visibility,
    dataGroup,
    timeRangesAndComparisons,
    metricNodeTitle,
    metricDefinition,
    metricResults,
    metricNodeId,
    hideTooltip,
    selectedTimeRangeIndex,
    setActiveMetricNodeId,
    setLocalSelectedNodeIds,
    onActiveTimeRangeChange
  }: {
    showConnectMetric: boolean;
    visibility: Record<CanvasVisibility, boolean>;
    dataGroup: DataGroup;
    timeRangesAndComparisons: TimeRangeAndComparison[];
    metricNodeTitle: string;
    metricDefinition: MetricDefinition;
    metricResults: Array<UseQueryResult<MetricsResponse, Error>>;
    metricNodeId: string;
    hideTooltip: boolean;
    selectedTimeRangeIndex: number;
    setActiveMetricNodeId: (nodeId: null | string) => void;
    setLocalSelectedNodeIds: (nodeIds: string[]) => void;
    onActiveTimeRangeChange: (timeRangeAndComparison: TimeRangeAndComparison) => void;
  } = _ref3;
  const [metricTooltipProps, setMetricTooltipProps] = useState<ChartTooltipProps | null>(null);
  const i18n = useTranslation();
  const metrics = useMetrics();
  const segmentFilterController = new SegmentFilterController();
  const {
    isFetching,
    data: metricResponse = null
  } = metricResults[selectedTimeRangeIndex];
  if (!visibility[CanvasVisibility.METRIC_VALUE] && !visibility[CanvasVisibility.SPARKLINE]) {
    return null;
  }
  if (isFetching) {
    return <div className={styles.nodeBody}>
        <SpinnerReact elref='nodeBodyLoading' className={clsx(styles.loader, {
        [styles.showAll]: visibility[CanvasVisibility.METRIC_VALUE] && visibility[CanvasVisibility.SPARKLINE],
        [styles.showMetricOnly]: visibility[CanvasVisibility.METRIC_VALUE] && !visibility[CanvasVisibility.SPARKLINE],
        [styles.showChartOnly]: !visibility[CanvasVisibility.METRIC_VALUE] && visibility[CanvasVisibility.SPARKLINE]
      })} size={SpinnerSize.Large} />
      </div>;
  }
  const comparisonValues: Array<{
    isLoading: boolean;
    liftValue: null | number;
    formattedLiftValue: null | string;
    comparisonTitle: string;
  }> = timeRangesAndComparisons.map((_ref4, index) => {
    let {
      title
    } = _ref4;
    const {
      isFetching: isLoading,
      data
    } = metricResults[index];
    if (!isLoading && !!data) {
      var _data$comparisons$;
      const comparisonValue = (_data$comparisons$ = data.comparisons[0]) === null || _data$comparisons$ === void 0 ? void 0 : _data$comparisons$.aggregate;
      const liftValue = comparisonValue === 0 ? 1 : (data.aggregate - comparisonValue) / Math.abs(comparisonValue);
      return {
        isLoading: false,
        liftValue,
        formattedLiftValue: formatPercent(Math.abs(liftValue), {
          abbreviate: true
        }),
        comparisonTitle: title
      };
    }
    return {
      isLoading: true,
      liftValue: null,
      formattedLiftValue: null,
      comparisonTitle: title
    };
  });
  const handleMouseMove = (ev: MouseEvent, index: number) => {
    // if metric response is still loading, do not show the tooltip
    if (hideTooltip || !metricResults[index].data || !metricDefinition) {
      return;
    }
    const metricSegment = getMetricSegment({
      metricDefinition,
      dataGroup,
      metricResponse: metricResults[index].data,
      i18n
    });
    const tooltipSections = computeMetricTooltipSections({
      metricName: metricNodeTitle,
      metricUnit: metricSegment.subLabel,
      formattedMetricValue: formatMetricValue({
        value: metricSegment.value,
        metricDefinition,
        metrics
      }),
      formattedComparisonValue: !isNil(metricSegment.comparisonValue) ? formatMetricValue({
        value: metricSegment.comparisonValue,
        metricDefinition,
        metrics
      }) : "",
      liftValue: comparisonValues[index].liftValue,
      timeRangeAndComparison: timeRangesAndComparisons[index],
      i18n
    });
    setMetricTooltipProps({
      titleColor: "var(--primary-color)",
      sections: tooltipSections,
      positioning: {
        clientX: ev.clientX,
        clientY: ev.clientY
      },
      renderInPortal: true
    });
  };
  const throttledMouseMove = throttle((ev, index) => handleMouseMove(ev, index), TOOLTIP_THROTTLE_TIME, {
    leading: true,
    trailing: false
  });
  const handleMetricMouseMove = (ev: MouseEvent) => {
    throttledMouseMove(ev, selectedTimeRangeIndex);
  };
  const handleMetricMouseLeave = () => {
    setMetricTooltipProps(null);
  };
  const handleLiftMouseMove = (ev: MouseEvent, index: number) => {
    ev.stopPropagation();
    throttledMouseMove(ev, index);
  };
  const handleLiftMouseLeave = () => {
    setMetricTooltipProps(null);
  };
  if (metricDefinition && metricResponse) {
    return <div className={styles.nodeBody}>
        <div className={styles.metricAggregate} onMouseMove={handleMetricMouseMove} onMouseLeave={handleMetricMouseLeave}>
          {visibility[CanvasVisibility.METRIC_VALUE] && <div className={styles.metric}>
              <MultiMetricChart hideValue={!visibility[CanvasVisibility.METRIC_VALUE]} hideSubValue selectedMetric={{
            value: 0,
            label: ""
          }} metrics={[getMetricSegment({
            metricDefinition,
            dataGroup,
            metricResponse,
            hideUnit: !visibility[CanvasVisibility.METRIC_VALUE],
            i18n
          })]} metricFormatter={(_, value) => formatMetricValue({
            value,
            metricDefinition,
            metrics
          })} metricSize={MetricChartSize.Medium} segmentFilterController={segmentFilterController} />
            </div>}
          {visibility[CanvasVisibility.SPARKLINE] && <div>
              <SparklineChart timeseries={metricResponse.timeseries} comparisonTimeseries={metricResponse.comparisons[0].timeseries} />
            </div>}
          {visibility[CanvasVisibility.METRIC_VALUE] && <div className={styles.metricLifts}>
              {comparisonValues.map((_ref5, index) => {
            let {
              isLoading,
              liftValue,
              formattedLiftValue,
              comparisonTitle
            } = _ref5;
            return <div key={index} className={clsx(styles.metricLiftContainer, {
              [styles.selected]: index === selectedTimeRangeIndex
            })} onClick={ev => {
              ev.stopPropagation();
              if (!isLoading) {
                onActiveTimeRangeChange(timeRangesAndComparisons[index]);
              }
            }} onMouseMove={ev => handleLiftMouseMove(ev, index)} onMouseLeave={handleLiftMouseLeave}>
                  <div className={styles.metricLiftLabel}>{comparisonTitle}</div>
                  <div>
                    {isLoading ? <SpinnerReact size={SpinnerSize.Small} /> : <>
                        {liftValue !== null && formattedLiftValue !== null && <div className={clsx(styles.metricLiftPercent, {
                    [styles.isPositive]: liftValue > 0,
                    [styles.long]: formattedLiftValue.length > 6
                  })}>
                            <SvgIconReact className={styles.arrowIcon} icon={liftValue > 0 ? "arrow-up" : "arrow-down"} />
                            <span>{formattedLiftValue}</span>
                          </div>}
                      </>}
                  </div>
                </div>;
          })}
            </div>}
        </div>
        {!hideTooltip && metricTooltipProps && <ChartTooltip {...metricTooltipProps} />}
      </div>;
  }
  return <div className={styles.nodeBody} data-sentry-component="NodeBody" data-sentry-source-file="index.tsx">
      <div className={styles.metricEmpty}>
        {visibility[CanvasVisibility.METRIC_VALUE] && <>
            <div className={styles.emptyBigNumber}>
              <div className={styles.block} />
              <div className={styles.block} />
              <div className={styles.block} />
            </div>
            <div className={styles.emptyLabel}>
              <div className={styles.block} />
              <div className={styles.block} />
              <div className={styles.block} />
            </div>
          </>}
        {visibility[CanvasVisibility.SPARKLINE] && <EmptyLineChart />}
        {showConnectMetric ? <ButtonReact className={styles.connectBtn} theme={ButtonTheme.Secondary} icon='plus' onClick={ev => {
        // need to stop propagation to prevent node being selected
        ev.stopPropagation();
        ev.preventDefault();
        setActiveMetricNodeId(metricNodeId);
        setLocalSelectedNodeIds([metricNodeId]);
      }}>
            Connect Metric
          </ButtonReact> : <div className={styles.spacing} />}
      </div>
    </div>;
}
function NodeHandles(_ref6: {
  id: string;
  activeEdge: null | AppEdge;
  isSelected: boolean;
}) {
  let {
    id,
    activeEdge,
    isSelected
  }: {
    id: string;
    activeEdge: null | AppEdge;
    isSelected: boolean;
  } = _ref6;
  const connection = useConnection();
  const isTargetNode = connection.inProgress && connection.fromNode.id !== id;
  return <>
      {!connection.inProgress && <>
          {[Position.Top, Position.Bottom, Position.Left, Position.Right].map(position => <CustomHandle key={"".concat(position, "-source")} type={"source"} position={position} isSelected={isSelected} showHandle={isSelected || (activeEdge === null || activeEdge === void 0 ? void 0 : activeEdge.source) === id && (activeEdge === null || activeEdge === void 0 ? void 0 : activeEdge.sourceHandle) === "".concat(position, "-source")} />)}
        </>}
      {(!connection.inProgress || isTargetNode) && <>
          {[Position.Top, Position.Bottom, Position.Left, Position.Right].map(position => <CustomHandle key={"".concat(position, "-target")} type={"target"} position={position} isSelected={isSelected} showHandle={!isSelected && isTargetNode || (activeEdge === null || activeEdge === void 0 ? void 0 : activeEdge.target) === id && (activeEdge === null || activeEdge === void 0 ? void 0 : activeEdge.targetHandle) === "".concat(position, "-target")} />)}
        </>}
    </>;
}
function TimeRangesAndComparisonsSelect(_ref7: {
  positionParent: HTMLElement;
  isOpen: boolean;
  hasTimeRangesAndComparisonsChanged: boolean;
  timeRangesAndComparisons: TimeRangeAndComparison[];
  asOfDate: string;
  onTimeRangeAndComparisonChange: (newTimeRangesAndComparisons: TimeRangeAndComparison[]) => void;
  onTimeRangeAndComparisonReset: () => void;
  onMenuChange: (isOpen: boolean) => void;
}) {
  let {
    positionParent,
    isOpen,
    hasTimeRangesAndComparisonsChanged,
    timeRangesAndComparisons,
    asOfDate,
    onTimeRangeAndComparisonChange,
    onTimeRangeAndComparisonReset,
    onMenuChange
  }: {
    positionParent: HTMLElement;
    isOpen: boolean;
    hasTimeRangesAndComparisonsChanged: boolean;
    timeRangesAndComparisons: TimeRangeAndComparison[];
    asOfDate: string;
    onTimeRangeAndComparisonChange: (newTimeRangesAndComparisons: TimeRangeAndComparison[]) => void;
    onTimeRangeAndComparisonReset: () => void;
    onMenuChange: (isOpen: boolean) => void;
  } = _ref7;
  return <DropMenuReact open={isOpen} positionParent={positionParent} direction={Directions.UpCenter} disableAutoFocus onChange={ev => onMenuChange((ev as VisibilityChangeEvent).detail.state === Visibility.Open)} data-sentry-element="DropMenuReact" data-sentry-component="TimeRangesAndComparisonsSelect" data-sentry-source-file="index.tsx">
      <div onClick={ev => ev.stopPropagation()} // to prevent opening QB panel when clicking inside
    onMouseDown={ev => ev.stopPropagation()} // to prevent pane drag when dragging inside
    >
        <TimeRangesAndComparisonsMenu timeRangesAndComparisons={timeRangesAndComparisons} asOfDate={asOfDate} hasTimeRangesAndComparisonsChanged={hasTimeRangesAndComparisonsChanged} forSingleMetric onTimeRangeAndComparisonChange={onTimeRangeAndComparisonChange} onReset={onTimeRangeAndComparisonReset} data-sentry-element="TimeRangesAndComparisonsMenu" data-sentry-source-file="index.tsx" />
      </div>
    </DropMenuReact>;
}
function ChartPreview(_ref8: {
  isOpen: boolean;
  additionalFilters: FilterClauseType[];
  dateRange: DateRange;
  metricNodeTitle: string;
  metricNodeData: MetricNodeData;
  metricResults: Array<UseQueryResult<MetricsResponse, Error>>;
  selectedTimeRangeIndex: number;
  positionParent: HTMLElement;
  onInsightsReportOpen: () => void;
  onMenuChange: (isOpen: boolean) => void;
}) {
  let {
    isOpen,
    additionalFilters,
    dateRange,
    metricNodeTitle,
    metricNodeData,
    metricResults,
    selectedTimeRangeIndex,
    positionParent,
    onInsightsReportOpen,
    onMenuChange
  }: {
    isOpen: boolean;
    additionalFilters: FilterClauseType[];
    dateRange: DateRange;
    metricNodeTitle: string;
    metricNodeData: MetricNodeData;
    metricResults: Array<UseQueryResult<MetricsResponse, Error>>;
    selectedTimeRangeIndex: number;
    positionParent: HTMLElement;
    onInsightsReportOpen: () => void;
    onMenuChange: (isOpen: boolean) => void;
  } = _ref8;
  const segmentFilterController = new SegmentFilterController();
  const {
    metricDefinition,
    metricFilters,
    metricDataGroupId
  } = metricNodeData;
  const chartPreviewTitle = "".concat(metricNodeTitle || "New Metric", " Trends");
  const {
    data: metricResponse = null
  } = metricResults[selectedTimeRangeIndex];
  if (!metricDefinition || !metricResponse) {
    return null;
  }
  const hasMetricResults = hasResults(metricResponse);
  return <DropMenuReact positionParent={positionParent} open={isOpen} disableAutoFocus direction={Directions.UpCenter} onChange={ev => onMenuChange((ev as VisibilityChangeEvent).detail.state === Visibility.Open)} data-sentry-element="DropMenuReact" data-sentry-component="ChartPreview" data-sentry-source-file="index.tsx">
      <SegmentFilterControllerProvider value={segmentFilterController} data-sentry-element="SegmentFilterControllerProvider" data-sentry-source-file="index.tsx">
        <div className={styles.metricChartPreview} onClick={ev => ev.stopPropagation()}>
          <div className={styles.header}>
            <SvgIconReact icon='brand-insights' data-sentry-element="SvgIconReact" data-sentry-source-file="index.tsx" />
            <span className={styles.name}>{chartPreviewTitle}</span>
            <ButtonReact tabIndex={-1} theme={ButtonTheme.Tertiary} icon='share' role='button' onClick={onInsightsReportOpen} data-sentry-element="ButtonReact" data-sentry-source-file="index.tsx">
              Explore
            </ButtonReact>
          </div>
          {hasMetricResults ? <div className={styles.chart}>
              <InsightsLineChartReact loaded hideLegend paramsAndResults={{
            results: convertToInsightsQueryResults({
              metricsResponse: metricResponse
            }),
            params: convertToInsightsBookmarkParams({
              metricDefinition,
              metricFilters,
              metricResponse,
              additionalFilters,
              dateRange,
              dataGroupId: metricDataGroupId
            })
          }} segmentFilterController={segmentFilterController} />
            </div> : <NoDataHeroReact reportName={WarningHeroReportName.Events} reason={NoDataReason.Default} />}
        </div>
      </SegmentFilterControllerProvider>
    </DropMenuReact>;
}
const selector = (nodeId: string) => (state: AppState) => {
  var _state$canvas, _state$canvas2, _state$activeTimeRang, _state$ghostNode;
  return {
    isViewOnly: !((_state$canvas = state.canvas) !== null && _state$canvas !== void 0 && _state$canvas.can_update_basic),
    hasGhostNode: Boolean(state.ghostNode),
    isActive: state.localSelectedNodeIds.includes(nodeId),
    activeEdge: state.activeEdge,
    canvasId: (_state$canvas2 = state.canvas) === null || _state$canvas2 === void 0 ? void 0 : _state$canvas2.id,
    deleteNode: state.deleteNode,
    duplicateNode: state.duplicateNode,
    asOfDate: state.asOfDate,
    globalTimeRangesAndComparisons: state.timeRangesAndComparisons,
    globalFilters: state.globalFilters,
    selectedTimeRangeIndex: (_state$activeTimeRang = state.activeTimeRangeIndex[nodeId]) !== null && _state$activeTimeRang !== void 0 ? _state$activeTimeRang : 0,
    visibility: state.visibility,
    ghostNodeId: (_state$ghostNode = state.ghostNode) === null || _state$ghostNode === void 0 ? void 0 : _state$ghostNode.id,
    setActiveMetricNodeId: state.setActiveMetricNodeId,
    setLocalSelectedNodeIds: state.setLocalSelectedNodeIds,
    setMetricNodeName: state.setMetricNodeName,
    setMetricNodeTimeRangesAndComparisons: state.setMetricNodeTimeRangesAndComparisons,
    setActiveTimeRangeIndex: state.setActiveTimeRangeIndex,
    otherActive: state.liveblocks.others.find(other => other.presence.localSelectedNodeIds.includes(nodeId))
  };
};
export function MetricNode(_ref9: NodeProps<MetricNodeType>) {
  var _dataGroupStore$value, _otherActive$connecti, _data$metricName;
  let {
    id,
    data,
    selected,
    dragging,
    positionAbsoluteX,
    positionAbsoluteY,
    height
  }: NodeProps<MetricNodeType> = _ref9;
  const {
    projectId,
    workspaceId
  } = useParams<{
    projectId: string;
    workspaceId?: string;
  }>();
  const location = useLocation();
  // local state, no need to sync to store
  const [isEditingNodeName, setEditingNodeName] = useState<boolean>(false);
  const [isTimeRangesMenuOpen, setIsTimeRangesMenuOpen] = useState<boolean>(false);
  const [isChartPreviewOpen, setIsChartPreviewOpen] = useState<boolean>(false);
  const {
    isViewOnly,
    isActive,
    hasGhostNode,
    activeEdge,
    canvasId,
    deleteNode,
    duplicateNode,
    asOfDate,
    globalTimeRangesAndComparisons,
    globalFilters,
    selectedTimeRangeIndex,
    visibility,
    ghostNodeId,
    setActiveMetricNodeId,
    setLocalSelectedNodeIds,
    setMetricNodeName,
    setMetricNodeTimeRangesAndComparisons,
    setActiveTimeRangeIndex,
    otherActive
  } = useCanvasStore(useShallow(selector(id)));
  const updateNodeInternals = useUpdateNodeInternals();
  const isGhost = id === ghostNodeId;
  const parentRef = useRef<HTMLDivElement>(null);
  const metrics = useMetrics();
  const dataGroupStore = useDataGroups();
  const openInNewTab = useOpenInNewTab();
  useEffect(() => {
    dataGroupStore.fetch({
      projectId,
      workspaceId
    });
  }, [projectId, workspaceId]);

  // this is necessary because we conditionally remove handle for ghost node.
  // so once node is commited we need to updateNodeInternals to make handles available
  // https://reactflow.dev/api-reference/hooks/use-update-node-internals
  useEffect(() => {
    if (!isGhost) {
      updateNodeInternals(id);
    }
  }, [isGhost, id, updateNodeInternals]);
  const additionalFilters = getActiveBoardFilters(globalFilters).map(propertyFilter => {
    return insightsFilterAttrsFromPropertyFilter(_objectSpread({
      dataset: DATASET_MIXPANEL
    }, propertyFilter));
  });
  const timeRangesAndComparisons = data.timeRangesAndComparisons || globalTimeRangesAndComparisons;
  const metricResults = useQueries({
    queries: timeRangesAndComparisons.map(_ref10 => {
      let {
        timeRange,
        timeComparison
      } = _ref10;
      const metricRequest: MetricsRequest = buildMetricRequest({
        nodeData: data,
        asOfDate,
        dateRange: new DateRange(timeRange),
        timeComparison,
        additionalFilters,
        projectId: projectId!,
        workspaceId
      });
      const queryKey = metricsNodeQueryKeyPrefix(metricRequest);
      return {
        queryKey,
        queryFn: (_ref11: {
          signal: AbortSignal;
        }) => {
          let {
            signal
          }: {
            signal: AbortSignal;
          } = _ref11;
          return fetchMetricData(metricRequest, {
            abortSignal: signal,
            location,
            trackingProps: {
              canvas_id: canvasId,
              node_id: id
            }
          });
        },
        enabled: !!data.metricDefinition,
        staleTime: METRICS_REQUEST_STALE_TIME
      };
    })
  });
  const {
    data: metricResponse = null
  } = metricResults[selectedTimeRangeIndex];
  const dataGroup = dataGroupStore === null || dataGroupStore === void 0 || (_dataGroupStore$value = dataGroupStore.value) === null || _dataGroupStore$value === void 0 ? void 0 : _dataGroupStore$value[data.metricDataGroupId || ""];
  const isOtherActive = Boolean(otherActive);
  const otherColorKey = ((_otherActive$connecti = otherActive === null || otherActive === void 0 ? void 0 : otherActive.connectionId) !== null && _otherActive$connecti !== void 0 ? _otherActive$connecti : 0) % COLORS.length;
  const metricNodeTitle = getMetricNodeTitle({
    metricName: (_data$metricName = data.metricName) !== null && _data$metricName !== void 0 ? _data$metricName : "",
    metricDefinition: data.metricDefinition,
    metrics
  });
  const handleTimeRangeIndexChange = (newTimeRangeAndComparison: TimeRangeAndComparison) => {
    setActiveTimeRangeIndex(id, newTimeRangeAndComparison);
  };
  const handleTimeRangesAndComparisonsChange = (newTimeRangesAndComparisons: TimeRangeAndComparison[]) => {
    if (newTimeRangesAndComparisons.length < timeRangesAndComparisons.length) {
      // if a set of time range and comparison is removed, reset active time range index for this node
      setActiveTimeRangeIndex(id, null);
    }
    setMetricNodeTimeRangesAndComparisons(id, newTimeRangesAndComparisons);
  };
  const handleTimeRangesAndComparisonsReset = () => {
    setActiveTimeRangeIndex(id, null);
    setMetricNodeTimeRangesAndComparisons(id, null);
  };
  const handleInsightsReportOpen = async () => {
    if (!data.metricDefinition || !metricResponse || !projectId || !workspaceId) {
      return;
    }
    const url = await generateBookmarkUrl({
      metricDefinition: data.metricDefinition,
      metricFilters: data.metricFilters,
      metricResponse,
      additionalFilters,
      dateRange: new DateRange(timeRangesAndComparisons[selectedTimeRangeIndex].timeRange),
      dataGroupId: data.metricDataGroupId,
      projectId: parseInt(projectId, 10),
      workspaceId: parseInt(workspaceId, 10)
    });
    if (url) {
      openInNewTab(url, "_blank");
      mixpanel.track(TrackingEvent.ExploreMetric, {
        canvas_id: canvasId,
        node_id: id,
        node_data: data
      });
    }
  };
  const handleControlAction = (action: ControlBarAction) => {
    switch (action) {
      case ControlBarAction.Edit:
        setActiveMetricNodeId(id);
        break;
      case ControlBarAction.Rename:
        setEditingNodeName(true);
        break;
      case ControlBarAction.TimeRanges:
        setActiveMetricNodeId(null);
        setIsTimeRangesMenuOpen(true);
        break;
      case ControlBarAction.Duplicate:
        {
          duplicateNode(id, {
            x: positionAbsoluteX,
            y: positionAbsoluteY + height! + 3
          }, {
            setActiveOnDuplicatedNode: true,
            duplicateEdges: true,
            removeEdgesFromOriginalNode: false
          });
          break;
        }
      case ControlBarAction.Delete:
        setActiveMetricNodeId(null);
        deleteNode(id);
        break;
    }
  };
  const handleMetricNameUpdate = (updatedMetricName: string) => {
    setEditingNodeName(false);
    setMetricNodeName(id, updatedMetricName);
  };
  return <div tabIndex={0} key={id} elref={"metric-".concat(id)} ref={parentRef} className={clsx(styles.metricNode, {
    [styles.filled]: !!data.metricDefinition,
    [styles.active]: isActive,
    [styles.ghost]: isGhost,
    [styles.isOtherActive]: isOtherActive
  })} style={{
    '--box-shadow-color': rgbaStringFromHexString(COLORS[otherColorKey], 0.2),
    '--color': COLORS[otherColorKey]
  }} data-sentry-component="MetricNode" data-sentry-source-file="index.tsx">
      {!isGhost && <NodeHandles id={id} activeEdge={activeEdge} isSelected={selected} />}
      <NodeHeader metricNodeId={id} metricNodeTitle={metricNodeTitle} metricDefinition={data.metricDefinition} isEditing={isEditingNodeName} isViewOnly={isViewOnly} setIsChartPreviewOpen={setIsChartPreviewOpen} onMetricNameUpdate={handleMetricNameUpdate} onControlAction={handleControlAction} data-sentry-element="NodeHeader" data-sentry-source-file="index.tsx" />
      <ReactErrorBoundary data-sentry-element="ReactErrorBoundary" data-sentry-source-file="index.tsx">
        <NodeBody showConnectMetric={!(isActive || isGhost || isViewOnly)} visibility={visibility} dataGroup={dataGroup} timeRangesAndComparisons={timeRangesAndComparisons} metricNodeTitle={metricNodeTitle} metricDefinition={data.metricDefinition} metricResults={metricResults} metricNodeId={id} hideTooltip={dragging || hasGhostNode} selectedTimeRangeIndex={selectedTimeRangeIndex} setActiveMetricNodeId={setActiveMetricNodeId} setLocalSelectedNodeIds={setLocalSelectedNodeIds} onActiveTimeRangeChange={handleTimeRangeIndexChange} data-sentry-element="NodeBody" data-sentry-source-file="index.tsx" />
      </ReactErrorBoundary>
      {parentRef.current && <ViewportPortal>
          {!dragging && isChartPreviewOpen && <ChartPreview isOpen={isChartPreviewOpen} additionalFilters={additionalFilters} dateRange={new DateRange(timeRangesAndComparisons[selectedTimeRangeIndex].timeRange)} metricNodeTitle={metricNodeTitle} metricNodeData={data} metricResults={metricResults} selectedTimeRangeIndex={selectedTimeRangeIndex} positionParent={parentRef.current} onInsightsReportOpen={handleInsightsReportOpen} onMenuChange={setIsChartPreviewOpen} />}
          {isTimeRangesMenuOpen && <TimeRangesAndComparisonsSelect positionParent={parentRef.current} isOpen={isTimeRangesMenuOpen} hasTimeRangesAndComparisonsChanged={!isEqual(timeRangesAndComparisons, globalTimeRangesAndComparisons)} timeRangesAndComparisons={timeRangesAndComparisons} asOfDate={asOfDate} onTimeRangeAndComparisonChange={handleTimeRangesAndComparisonsChange} onTimeRangeAndComparisonReset={handleTimeRangesAndComparisonsReset} onMenuChange={setIsTimeRangesMenuOpen} />}
        </ViewportPortal>}
    </div>;
}